import { Fragment, type ReactNode, type SyntheticEvent } from 'react'
import { MapPin, Plane, Car, Building2 } from 'lucide-react'
import type { PageContent, VisitAttraction } from '../api/types'

const handleImgError = (e: SyntheticEvent<HTMLImageElement>) => {
  // Drop the broken img so the .vc-image gradient + icon fallback shows.
  e.currentTarget.style.display = 'none'
}

// The admin edits plain text; `**texto**` is the only markup we honour.
function withBold(text: string): ReactNode {
  return text.split(/\*\*(.+?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : <Fragment key={i}>{part}</Fragment>,
  )
}

const cardNumber = (index: number) => String(index).padStart(2, '0')

const AttractionCard = ({ data, index }: { data: VisitAttraction; index: number }) => (
  <article
    className="vc-card reveal-on-scroll"
    style={{ ['--i' as string]: index } as React.CSSProperties}
  >
    <div className="vc-image">
      {data.image && <img src={data.image} alt={data.alt} loading="lazy" onError={handleImgError} />}
      <MapPin className="vc-image-fallback-icon" size={32} aria-hidden />
    </div>
    <div className="vc-card-body">
      <span className="vc-number">{cardNumber(index + 2)}</span>
      <h3>{data.title}</h3>
      <p>{data.blurb}</p>
    </div>
  </article>
)

export function VisitComayaguaSection({ content }: { content: PageContent['visit'] }) {
  if (!content.enabled) return null
  const { feature, route } = content

  return (
    <section
      className="visit-comayagua-section"
      id="visit-comayagua"
      aria-labelledby="visit-heading"
    >
      <div className="vc-inner">
        {/* Hero headline */}
        <header className="vc-hero reveal-on-scroll">
          {content.eyebrow && <span className="eyebrow vc-eyebrow">{content.eyebrow}</span>}
          <h2 id="visit-heading">
            {content.title_prefix}
            {content.title_prefix && content.title_highlight ? ' ' : null}
            {content.title_highlight && <em>{content.title_highlight}</em>}
          </h2>
          {content.lede && <p className="vc-lede">{withBold(content.lede)}</p>}
        </header>

        {/* Stat bar */}
        {content.stats.length > 0 && (
          <ul className="vc-stats reveal-on-scroll" aria-label="Datos de la ciudad">
            {content.stats.map((stat, i) => (
              <li key={`${stat.label}-${i}`}>
                <span className="vc-stat-value">
                  {stat.value}
                  {stat.unit && <span className="vc-stat-unit">{stat.unit}</span>}
                </span>
                <span className="vc-stat-label">{stat.label}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Feature story — 01 */}
        {feature.title && (
          <div className="vc-feature reveal-on-scroll">
            <div className="vc-feature-image">
              <div className="vc-image vc-image--portrait">
                {feature.image && (
                  <img src={feature.image} alt={feature.alt} loading="lazy" onError={handleImgError} />
                )}
                <Building2 className="vc-image-fallback-icon" size={40} aria-hidden />
              </div>
            </div>
            <div className="vc-feature-body">
              <span className="vc-number">01</span>
              <h3>{feature.title}</h3>
              <p>{feature.blurb}</p>
              {feature.quote && <blockquote className="vc-quote">“{feature.quote}”</blockquote>}
            </div>
          </div>
        )}

        {/* Supporting cards — 02…n */}
        {content.cards.length > 0 && (
          <div className="vc-grid">
            {content.cards.map((card, i) => (
              <AttractionCard key={`${card.title}-${i}`} data={card} index={i} />
            ))}
          </div>
        )}

        {/* Cómo llegar — route timeline */}
        {route.length > 0 && (
          <div className="vc-route reveal-on-scroll" aria-labelledby="vc-route-title">
            <div className="vc-route-header">
              {content.route_eyebrow && <span className="eyebrow vc-eyebrow">{content.route_eyebrow}</span>}
              <h3 id="vc-route-title">{content.route_title}</h3>
            </div>
            <ol className="vc-route-timeline">
              {route.map((node, i) => (
                <li key={`${node.label}-${i}`} className="vc-route-node">
                  <div className="vc-route-dot" aria-hidden>
                    {i === 0 ? (
                      <Building2 size={18} />
                    ) : i === route.length - 1 ? (
                      <MapPin size={18} />
                    ) : i === 1 ? (
                      <Car size={18} />
                    ) : (
                      <Plane size={18} />
                    )}
                  </div>
                  <div className="vc-route-text">
                    <span className="vc-route-label">{node.label}</span>
                    {node.detail && <span className="vc-route-detail">{node.detail}</span>}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Closing CTA */}
        {content.cta_label && (
          <div className="vc-cta reveal-on-scroll">
            <a href="#rooms" className="primary-button">
              {content.cta_label}
            </a>
          </div>
        )}
      </div>
    </section>
  )
}
